import React, {createRef, useEffect, useState} from 'react';
import {Viewer} from './Viewer-Factory/Viewer';
import {useViewerStore} from "../../../../store/store";
import {useNavigate} from "react-router-dom";
import {useCurrentUser} from "../../../../contexts/UserContext";
import moduleServices from "../../../../services/moduleService";
import {getDesignVersionList} from "../../../../services/designVersionService";
import siteService from "../../../../services/siteService";
import getStateCodeFromStateName from "../../../../services/stateCodeService";
import {checkAndSetSetbackOverrideFlag, fetchSetbackInfo} from "./Controllers/PathwayControl";
import {LoadModelJson} from "./Controllers/Loader";
import {getSite} from "../../../../hooks/useLoadOriginalSite";
import {IoIosLock} from "react-icons/io";
import LockedDesignValidation from "../Modal/Modal-Content/LockedDesignValidation";
import {logger} from "../../../../services/loggingService";
import masterDataService from "../../../../services/masterDataService";
import {getPathnameIncludes} from "../../../../helpers/getPathname";
import {LAYER_OPTIONS} from "../../../../Constants";
import {populateRoofSquareData} from './Controllers/RoofSquareControl';
import {setDeactivateRoofArrInStoreFromDesignData} from "./Controllers/Common";
import {clickButtonWithRef, setPathname, toggleLayer} from '../../../../helpers/storeHelpers';
import {BuildingDesignData, ModuleListAPIResponseItem, ModuleTypeOptionData, RoofUserData} from '../../../../@types/types';
import {AlertType, EditorControlMode, HideObjectType, SelectType} from '../../../../@types/enums';
import packingSlipService from '../../../../services/packingSlipService';

let viewerInstance: Viewer

export const canvasContainerRef  = createRef<HTMLDivElement>();

const ThreeDViewer: React.FC = () => {

    const {user, hasCapability} = useCurrentUser();
    const navigate = useNavigate();

    const {
        sceneViewer,
        pathname,
        layerVisibilityState,
        isGhostObstructionUpdated,
        isHideAllGhostTrees,
        designList,
        selectedDesignIndex,
        loadingCompleted,
        accessoryBuildingList,
        buildingWise33PercentRuleModuleCountList,
        initialLoadLoadingContextRef,
        isNonFinalizedSavedDesign,
        roofPitchOverrideArr,
    } = useViewerStore();

    const [isDesignFetching, setIsDesignFetching] = useState(true);

    const keyDownListener = (e: KeyboardEvent) => {

        const isModalOpenInInterface = useViewerStore.getState().isModalOpenInInterface;
        const isTypeEvent = useViewerStore.getState().isTypeEvent;


        if (isTypeEvent) return;

        if (!pathname.includes('configure') || isModalOpenInInterface) {
            return;
        }

        if (e.ctrlKey) {
            if (hasCapability('Undo') && e.key === 'z') {
                //Undo
                //Ctrl + Z
                clickButtonWithRef("undoBtn");
            } else if (hasCapability('Redo') && e.key === 'y') {
                //Redo
                //Ctrl + Y
                clickButtonWithRef("redoBtn");
            }
        } else if (e.altKey) {
            if (e.key === 't') {
                //Alt + T
                viewerInstance.setupSelectionBoxHelper(SelectType.tree);
            }
        } else if (e.shiftKey) {
            if (hasCapability('Orbit') && e.key.toLowerCase() === 'o') {
                //Shift + O
                viewerInstance.setOrbitControlsMode(EditorControlMode.orbit);
            } else if (hasCapability('Pan') && e.key.toLowerCase() === 'p') {
                //Shift + P
                viewerInstance.setOrbitControlsMode(EditorControlMode.pan);
            } else if (e.key.toLowerCase() === 'h') {
                //Shift + H
                clickButtonWithRef("defaultPositionBtn")
            } else if (e.key === 'Delete') {
                //Shift + Delete
                clickButtonWithRef("clearSceneBtn")
            }
        } else if (e.key === 'Escape') {
            useViewerStore.setState({selRoofFaceArr: []});
            sceneViewer.removeSelectionBoxHelper();
            //if pathname is not tape-measure
            if (getPathnameIncludes('tape-measure')) {
                viewerInstance.resetDraw();
                viewerInstance.setDrawMode(false);
                viewerInstance.setAutoAlignMode(false);
                setPathname('/configure/roof');
            } else {
                viewerInstance.setDrawMode(false);
                viewerInstance.setAutoAlignMode(false);
            }
            const event = new Event("closeContextMenu");
            document.getElementById("canvasContainer")?.dispatchEvent(event);
            viewerInstance.deselectAllSelectedObjectsAndEndTask();
        } else if (e.key === 'Delete') {
            viewerInstance.setModuleSetting('delete');
        } else if (e.key?.toLowerCase() === 'p') {
            //P - Push Pull
            clickButtonWithRef("pushPullToolBtn")
        } else if (hasCapability('Tape_Measure') && e.key.toLowerCase() === 'd') {
            //Q - Measure
            clickButtonWithRef("btnMeasure")
        } else if (hasCapability('X-Ray_View') && e.key.toLowerCase() === 'k') {
            //K - X Ray
            clickButtonWithRef("btnXRay")
        } else {
            // Switch Menu Shortcuts
            if (hasCapability('What_If') && e.key.toLowerCase() === 'w') {
                //W - What If
                sceneViewer.setMenu('what-if');
            } else if (e.key.toLowerCase() === 'o') {
                //O -Obstruction
                sceneViewer.setMenu('obstruction');
            } else if (e.key.toLowerCase() === 'r') {
                //R- Roof
                sceneViewer.setMenu('roof');
            } else if (e.key.toLowerCase() === 't') {
                //T- Tree
                sceneViewer.setMenu('tree');
            } else if (e.key.toLowerCase() === 'm') {
                //M - Module
                sceneViewer.setMenu('modules');
            } else if (e.key.toLowerCase() === 'i' && hasCapability("Irradiance")) {
                //I - Irradiance
                sceneViewer.setMenu('irradiance');
            } else if (e.key.toLowerCase() === 's') {
                //S - Solar Access
                sceneViewer.setMenu('solar-access');
            } else if (e.key.toLowerCase() === 'e' && hasCapability('EPC_Calculations')) {
                //E - EPC
                sceneViewer.setMenu('epc');
            } else if (e.key.toLowerCase() === 'f' && (hasCapability('Surface Draw') || hasCapability('Surface_Draw'))) {
                //F - Ground Mount
                sceneViewer.setMenu('surface-draw');
            } else if (e.key.toLowerCase() === 'b' && hasCapability("View_Building_Attributes")) {
                //B - Building
                sceneViewer.setMenu('building');
            } else if (e.key.toLowerCase() === 'x' && hasCapability("Layers")) {
                //X - X-Ray Toggle for Trees
                const prevState = useViewerStore.getState().layerVisibilityState[LAYER_OPTIONS.MODEL.TREE]
                toggleLayer(LAYER_OPTIONS.MODEL.TREE, !prevState);
                viewerInstance.setVisibleGroup(LAYER_OPTIONS.MODEL.TREE, !prevState);
            }

        }
    };

    useEffect(() => {
        try {
            masterDataService
                .getMasterDataList()
                .then(async (masterDataAPIResponse) => {
                    useViewerStore.setState({masterDataList: masterDataAPIResponse});
                })
                .catch((error) => {
                    logger.error(error);
                });
        } catch (e) {
            logger.error(e);
        }
        useViewerStore.setState({isTreeLabelVisible: false})
    }, []);

    useEffect(() => {
        const loadingContext = initialLoadLoadingContextRef


        //Instantiate a new Scene from Viewer Factory
        viewerInstance = new Viewer();

        //Set ViewerState for parent components to access Viewer Instance
        useViewerStore.setState({sceneViewer: viewerInstance});
        //Navigating to the editor page
        setPathname("/configure/roof");
        viewerInstance.selectType = SelectType.roof;

        if (!loadingContext) return;

        const handleWebGLContextLost = () => {
            window.location.reload();
        };

        viewerInstance.renderer.domElement.addEventListener('webglcontextlost', handleWebGLContextLost, false);

        if (!user?.user_Id) {
            return;
        }

        // anonymous async caller
        (async () => {

            try {

                const urlParams = new URLSearchParams(window.location.search);
                const sfid = urlParams.get('sfid');

                if (!sfid || sfid === '' || sfid === null) {
                    alert("No Site Found / Invalid SalesForceId");
                    return
                }

                loadingContext.setMessage({message: 'Getting Site Data...'});

                const opportunity = await siteService.getOpportunity(sfid);
                const response = await siteService.postSitesSearch(sfid, 1, [], {}, false, false);

                if (response.Message === "No records found") {
                    throw new Error("No Site Found for the given SalesForceId, Please check the SalesForceId and try again.");
                }

                const site = response.Data[0];
                useViewerStore.setState({productType: site.ProductCategory});

                loadingContext.setMessage({message: 'Processing Site Data...'});
                const [state, zipCode] = site.State.split("-");
                const stateCode = getStateCodeFromStateName(state);

                // Formatting the address
                let address = site.Address;
                if (site.Address?.includes(",")) {
                    const addressFirstPartTrimmed = site.Address.split(",")[0];
                    const fullAddress = `${addressFirstPartTrimmed}, ${site.City}, ${stateCode}`;
                    address = `${fullAddress.split(",")[0]}, ${fullAddress.split(",")[1]}, ${fullAddress.split(",")[2].split("-")[0]}`;
                }

                document.title = `${address} - OneDraw`;

                const stage: string = site.Stage?.split(':')[0];

                // Warning if Stage 4 or later and user is not Admin/Sales Op/RSA
                const canModifyStage4 = hasCapability('Stage_4_Post_Contract_Finalize');
                const canFinalize = hasCapability('Finalize');
                if (canFinalize && (stage && Number(stage) >= 4) && !canModifyStage4) {
                    alert("Alert: This opportunity is in stage 4 or later, therefore no modification will be saved");
                }

                const isUserHaveSetbackOverrideCapability = hasCapability("Add_Panels_Past_Setbacks");

                if(isUserHaveSetbackOverrideCapability){
                    useViewerStore.setState({isUserHaveCapabilityToAddPanelsPastSetbacks : true});
                }
                else{
                    useViewerStore.setState({isUserHaveCapabilityToAddPanelsPastSetbacks : false});
                }

                sessionStorage.setItem('OppurtunityId', site.Account_Id);
                sessionStorage.setItem('SalesForceId', site.SalesForceId);
                sessionStorage.setItem('stateCode', stateCode);
                sessionStorage.setItem('postalCode', zipCode);
                sessionStorage.setItem('directLead', opportunity.DirectLeadSfId);
                sessionStorage.setItem('customerNumber', opportunity.CustomerNumber ?? '');
                sessionStorage.setItem('opportunityName', site.Opportunity_Name);
                sessionStorage.setItem('city', site.City);
                sessionStorage.setItem('currentStage', stage);
                sessionStorage.setItem('stageStatus', site.Stage_Status);
                sessionStorage.setItem('Utility_Company', site.Utility_Company);
                sessionStorage.setItem('Multiplier', site.Multiplier);

                useViewerStore.setState({siteAddress: address, loadingCompleted: false});
                loadingContext.setMessage({message: 'Fetching Modules...'});

                const moduleListAPIResponse = await moduleServices.getModuleList(stateCode);
                if(!moduleListAPIResponse.Modules && moduleListAPIResponse.response.status !== 200) {
                    throw new Error(moduleListAPIResponse.Message || `Failed to fetch Module List, API Error : Status Code ${moduleListAPIResponse.response.status}`);

                }
                const moduleList = moduleListAPIResponse.Modules.map((item: ModuleListAPIResponseItem, index: number) => ({
                    key: index,
                    text: item.Name,
                    value: item.Name,
                    power: item.Wattage,
                    w: item.WidthInMm / 1000,
                    h: item.LengthInMm / 1000,
                    manufacturer: item.Manufacturer,
                }));

                const defaultModule = moduleList.find((module: ModuleTypeOptionData) => module.text === moduleListAPIResponse.DefaultModule.Name);

                useViewerStore.setState({moduleList});
                useViewerStore.setState({activeModule: defaultModule});

                loadingContext.setMessage({message: 'Pulling site design...'});
                const token = await siteService.getOneDrawEntraIdToken();
                await siteService.loadSiteDesigns(site.Account_Id, token);

                useViewerStore.setState({designList : []});

                loadingContext.setMessage({message: 'Getting Site Designs...'});

                const designList = await getDesignVersionList(sfid);

                packingSlipService.fetchFinancePartnerDetails().then(details => {
                    useViewerStore.setState({ financePartnerDetails: details });
                });

                if (designList === null) {
                    setIsDesignFetching(false);
                    useViewerStore.setState({accessoryBuildingList: []});
                    await getSite(navigate)
                    return;
                }

                const design = designList[0];

                useViewerStore.setState({
                    selectedDesignIndex: 0, 
                    designList : designList,
                });

                
                setIsDesignFetching(false);
                const data = await siteService.retrieveSiteDesignDetailsWithDesignData(sfid, design.Design_No, design.Version_No);

                const designData = data.DesignDataList[0].Design_Data;
                useViewerStore.setState({designID: data.DesignDataList[0].DesignID});

                const accessoryBuildingList = designList[0].Buildings.filter((building: BuildingDesignData) => building.Principal_Structure === false).map((building: BuildingDesignData) => building.Building_Name);
                useViewerStore.setState({accessoryBuildingList: accessoryBuildingList});

                setDeactivateRoofArrInStoreFromDesignData(designList,0)


                loadingContext.setMessage({message: 'Fetching Setback Info...'});
                await fetchSetbackInfo(sfid, sceneViewer);


                checkAndSetSetbackOverrideFlag(designData)

                loadingContext.setMessage({message: 'Drawing Site...'});

                await LoadModelJson(viewerInstance, designData, 'glb');
            } catch (e: unknown) {
                logger.error(e);
                let message = "Unknown error";
                if (e instanceof Error) {
                    message = e.message;
                } else if (typeof e === "string") {
                    message = e;
                } else if (typeof e === "object" && e !== null && "message" in e) {
                    message = String((e as any).message);
                } else {
                    message = "An unexpected error occurred during site loading.";
                }
                viewerInstance.viewerAlert({
                    show: true, title: "Loading Error", message, messageType: AlertType.error, isModal: true
                });
            } finally {
                if (loadingContext) {
                    loadingContext.dispose();
                }
                window.addEventListener('keydown', keyDownListener, false);
                window.addEventListener('resize', viewerInstance.resizeCanvas);
            }
        })();

        return () => {
            viewerInstance.renderer.domElement.removeEventListener("webglcontextlost", handleWebGLContextLost, false);
            viewerInstance.renderer.domElement.remove();
            window.removeEventListener('keydown', keyDownListener, false);
            window.removeEventListener('resize', viewerInstance.resizeCanvas);
            sessionStorage.setItem('OppurtunityId', "");
            sessionStorage.setItem('SalesForceId', "");
            sessionStorage.setItem('stateCode', "");
            sessionStorage.setItem('postalCode', "");
            sessionStorage.setItem('directLead', "");
            sessionStorage.setItem('opportunityName', "");
            sessionStorage.setItem('currentStage', "");
            sessionStorage.setItem('stageStatus', "");
            useViewerStore.setState({siteAddress: ''});
        };
    }, [user, initialLoadLoadingContextRef]);

    useEffect(() => {
        setPathname(pathname);
        viewerInstance.enableAllLayers(pathname.includes('modules'));
        let selectType = viewerInstance.getSelectTypeFromPath(pathname)
        const measureMode = pathname.includes('measure');
        viewerInstance.hideObject(HideObjectType.GhostObst, true);
        if (!pathname.includes('roof')) {
            viewerInstance.toggleRoofTag(false);
            useViewerStore.setState({
                isRoofTagVisible: false, isMoveRoofActive: false
            });
        }

        if (!pathname.includes('tree')) {
            useViewerStore.setState({
                isHideAllSelectedTrees: false,
                isTreePerimeterToolActive: false,
                isMoveTreeActive: false,
                isAddTreeActive: false
            });
            viewerInstance.hideObject(HideObjectType.Seltree, false);
            viewerInstance.showRoofSquirrel(false);
        }

        if (pathname.includes('modules')) {
            viewerInstance.setVisibleGroup(LAYER_OPTIONS.MODEL.TREE, false);
            viewerInstance.hideObject(HideObjectType.GhostTree, true);
        } else if (layerVisibilityState.tree) {
            viewerInstance.setVisibleGroup(LAYER_OPTIONS.MODEL.TREE, true);
        }

        if (!pathname.includes('obstruction')) {
            useViewerStore.setState({
                isHideAllGhostObstructions: true, isMoveObstructionActive: false, isAddObstructionActive: false
            });

        }

        if (!pathname.includes('price-calculation')) {
            viewerInstance.toggleTreeMultiplierLabel(false);
            viewerInstance.toggleModuleMultiplierLabel(false);
            useViewerStore.setState({
                isTreeMultiplierLabelVisible: false, isModuleMultiplierLabelVisible: false,
            });
        }

        if (pathname.includes('price-calculation')) {
            viewerInstance.toggleTreeMultiplierLabel(true);
            // viewerInstance.toggleModuleMultiplierLabel(false);
            useViewerStore.setState({
                isTreeMultiplierLabelVisible: true, // isModuleMultiplierLabelVisible: false,
            });
        }

        if (pathname.includes('obstruction')) {
            viewerInstance.setVisibleGroup(LAYER_OPTIONS.MODEL.TREE, false);
            useViewerStore.setState({
                isHideAllGhostObstructions: true
            });

        }


        viewerInstance.setSelectType(selectType);
        viewerInstance.setMeasureMode(measureMode);
        viewerInstance.setAutoAlignMode(false);
        if(isHideAllGhostTrees) viewerInstance.hideObject(HideObjectType.GhostTree, isHideAllGhostTrees);
        useViewerStore.setState({isTapandHold: false})
        useViewerStore.setState({isTabandHoldUp: false});
    }, [pathname])

    useEffect(() => {
        if (loadingCompleted) {


            if (selectedDesignIndex >= 0 && designList[0]?.Design_Certification_Status && designList[0]?.Design_Certification_Status !== "Not Certified" && designList[0]?.Design_Certification_Status !== "Certified Design Override" && designList[0]?.Design_Certification_Status !== null) {
                const isUnlockedSite = hasCapability("Pre_Installed_Certified") || hasCapability("Install_Change_Certified") || hasCapability("As_Built_Certified") || hasCapability("Certified_Design_Override")
                if (!isUnlockedSite) {
                    setTimeout(() => {
                        sceneViewer?.viewerAlert({
                            show: true,
                            title: <span style={{display: "flex"}}><IoIosLock
                                size={25}/> {designList[0]?.Design_Certification_Status}</span>,
                            message: <LockedDesignValidation
                                certification_status={designList[0]?.Design_Certification_Status}/>,
                            messageType: "success",
                            isModal: true
                        })
                    }, 5000);
                }
            }
            // useViewerStore.setState({loadingCompleted: false})
        }
    }, [loadingCompleted])

    useEffect(() => {
        viewerInstance.hideObject(HideObjectType.GhostObst, true);
        useViewerStore.setState({isHideAllGhostObstructions: true});
    }, [isGhostObstructionUpdated]);

    useEffect(() => {
        if (isDesignFetching) return;
        const selectedDesign = designList[selectedDesignIndex];
        populateRoofSquareData(selectedDesign);
    }, [buildingWise33PercentRuleModuleCountList, selectedDesignIndex, isNonFinalizedSavedDesign, accessoryBuildingList]);

    return (<div ref={canvasContainerRef} id={'canvasContainer'}></div>);
}

export default ThreeDViewer;